import React, { FC, useMemo } from 'react';
import { Typography, TypographyProps } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { ExpressionValue } from '@shared/models/project.model';
import {
  quantityUnitToMathJSUnit,
  QuantityUnit,
} from '@shared/models/unit.model';
import { getDisplayValueData } from '@shared/helpers/display/get-display-value-data.helper';
import { getDisplayNumber } from '@shared/helpers/display/get-display-number.helper';
import { UNICODE } from '@shared/models/unicode.model';
import { KineticNumberDisplay } from '@ui/KineticNumberDisplay';

type ExpressionTypographyProps = {
  value: ExpressionValue | number | undefined;
  unit?: QuantityUnit | 'none';
  displayUnit?: string;
  disabled?: boolean;
  isFallback?: boolean;
  variant?: TypographyProps['variant'];
};

/**
 * Displays an expression result with its resolved unit and an animated value transition.
 */
export const ExpressionTypography: FC<ExpressionTypographyProps> = ({
  value,
  unit,
  displayUnit,
  disabled,
  isFallback,
  variant = 'inherit',
}) => {
  const { classes, cx } = useStyles();
  const resolved = typeof value === 'number' ? value : (value?.resolved ?? 0);

  // Show "-" when fallback is 0 (empty)
  const useDashFallback = isFallback && !resolved;

  const formatted = useMemo(() => {
    if (displayUnit !== undefined && displayUnit !== 'none') {
      // we cannot use getDisplayValueData, because displayUnit can be anything
      // just format the value with getDisplayNumber and leave unit as displayUnit
      const value = useDashFallback ? '-' : getDisplayNumber(resolved);
      return { value, valueAsNumber: resolved, unit: displayUnit };
    } else if (useDashFallback) {
      return { value: '-', valueAsNumber: resolved, unit };
    } else {
      return getDisplayValueData({
        value: resolved,
        unit: quantityUnitToMathJSUnit(unit),
      });
    }
  }, [useDashFallback, resolved, unit, displayUnit]);

  return (
    <Typography
      component="span"
      variant={variant}
      className={cx(
        isFallback && classes.discrete,
        disabled && classes.disabled,
      )}
      sx={{
        whiteSpace: 'nowrap',
        pr: unit ? 2 : 0,
      }}
    >
      {useDashFallback ? (
        formatted.value
      ) : (
        <KineticNumberDisplay
          key={formatted.unit}
          value={formatted.valueAsNumber}
          format={getDisplayNumber}
        />
      )}
      <Typography variant="caption" component="span">
        {UNICODE.SPACES.NON_BREAKING}
        {formatted.unit}
      </Typography>
    </Typography>
  );
};

const useStyles = makeStyles()(({ palette }) => ({
  discrete: {
    color: palette.text.disabled,
  },
  disabled: {
    color: palette.text.disabled,
  },
}));

export default ExpressionTypography;
